// batch-65 voorraad-besteladvies (FO-481) — pure rekenregels.
//
// Géén DB-calls, géén Fastify, géén `node:*`. De cron, de beoordelings-routes en
// de backoffice delen deze functies, zodat het adviesaantal, het minimum-
// voorstel, de stilteperiode en het run-venster op één plek zijn vastgelegd.
//
// Hergebruik (CC.0): de tekortsom komt uit `berekenBesteladvies`
// (inkoop-besteltraject.ts), het aanvul-niveau (gewenst, anders minimum) uit
// `berekenAanvulAdvies` en de drempel uit `isLaagInVoorraad` (@streep/types) -
// dezelfde grens als het VOORRAAD_LAAG-signaal, de alerts en de KPI.

import { berekenAanvulAdvies, isLaagInVoorraad } from '@streep/types'
import { berekenBesteladvies } from './inkoop-besteltraject'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'
// Kalenderdagen optellen bij een jjjj-mm-dd: dezelfde helper als de telefonie-
// module (geen tweede implementatie).
import { plusDagenYmd } from './telefonie'

/** Periode waarover het verbruik voor advies en minimumvoorstel geteld wordt. */
export const VERBRUIK_PERIODE_DAGEN = 90

// ────────────────────────────────────────────────────────────────────────────
// Adviesaantal
// ────────────────────────────────────────────────────────────────────────────

/**
 * Rond een aantal naar boven af op een veelvoud van de besteleenheid
 * (`bestelAantal`, de verpakkingsgrootte). Zonder (of met een besteleenheid van
 * 1) blijft het aantal staan. 0 of minder levert 0.
 */
export function rondAfOpBesteleenheid(aantal: number, bestelAantal: number | null | undefined): number {
  if (!Number.isFinite(aantal) || aantal <= 0) return 0
  const eenheid = besteleenheid(bestelAantal)
  return Math.ceil(aantal / eenheid) * eenheid
}

/** De besteleenheid als heel getal >= 1 (zonder, of 1 of minder: per stuk). */
export function besteleenheid(bestelAantal: number | null | undefined): number {
  return bestelAantal != null && bestelAantal > 1 ? Math.floor(bestelAantal) : 1
}

/** Beschikbare voorraad = max(0, saldo - gereserveerd); negatief bestaat niet (FO-467). */
export function berekenBeschikbaar(saldo: number, gereserveerd: number): number {
  return Math.max(0, saldo - Math.max(0, gereserveerd))
}

export interface BesteladviesRegelInput {
  /** Fysiek totaal (bulk: som saldi over alle locaties; uniek: exemplaren OP_VOORRAAD). */
  saldo: number
  gereserveerd: number
  minimumVoorraad: number
  maximumVoorraad: number | null | undefined
  /** Open bestellingen (besteld - ontvangen) plus ontvangen-nog-niet-binnengeboekt. */
  inBestelling: number
  /** Besteleenheid (verpakkingsgrootte); het advies wordt hierop naar boven afgerond. */
  bestelAantal: number | null | undefined
}

export interface BesteladviesRegelUitkomst {
  beschikbaar: number
  /** Beschikbaar op of onder het (ingestelde, > 0) minimum (fysiek, zonder wat onderweg is). */
  onderMinimum: boolean
  /**
   * batch-69 (FO-521 §C11) — de bestelpositie: beschikbaar + wat onderweg is. Het
   * advies ontstaat pas als DIE op of onder het minimum ligt; staat er al genoeg
   * in bestelling om boven het minimum te komen, dan wordt er niet bijbesteld.
   */
  positie: number
  positieOnderMinimum: boolean
  /**
   * Het niveau waar naartoe aangevuld wordt: exact `berekenAanvulAdvies` (het
   * maximum, of het minimum zonder geldig maximum).
   */
  aanvullenTot: number
  /** True als er een geldig maximum (op of boven het minimum) staat. */
  heeftMaximum: boolean
  /** max(0, aanvullenTot - positie). */
  tekort: number
  /**
   * Naar boven afgerond op de besteleenheid en nooit boven het plafond
   * (aanvullenTot + besteleenheid - 1); 0 = regel weglaten.
   */
  adviesAantal: number
}

/**
 * Adviesaantal voor één artikel (FO-481 §A1, batch-69 FO-521 §C11):
 *
 *   positie = max(0, beschikbaar) + in bestelling
 *   advies  = aanvullenTot - positie, afgerond op de besteleenheid,
 *             alleen als positie OP of onder het minimum ligt,
 *             en nooit boven aanvullenTot + (besteleenheid - 1).
 *
 * `aanvullenTot` komt 1-op-1 uit de gedeelde `berekenAanvulAdvies` (het
 * maximum, of het minimum zonder geldig maximum): het advies, het
 * VOORRAAD_LAAG-signaal, de alerts en de artikelpagina noemen zo hetzelfde getal
 * (één waarheid). Bewust GEEN extra verbruik tijdens de levertijd erbovenop: het
 * minimum is het bestelpunt en dekt de levertijd al (zie `stelMinimumVoor`).
 *
 * De drempel is `<=` (spec 221 schrijft `<`): op precies het minimum vuurt ook
 * het VOORRAAD_LAAG-signaal, en een signaal mag nooit naar een advies verwijzen
 * dat er niet is. Wat onderweg is telt mee in de positie (spec 221 §C11): een
 * artikel waarvoor al genoeg in bestelling staat, krijgt geen tweede advies.
 * `alleenOnderMinimum: false` (een handmatige toevoeging) slaat de drempel over.
 * Is het advies 0 of minder, dan is het 0: de regel vervalt (§5.3).
 */
export function berekenBesteladviesRegel(
  input: BesteladviesRegelInput,
  opties: { alleenOnderMinimum?: boolean } = {},
): BesteladviesRegelUitkomst {
  const alleenOnderMinimum = opties.alleenOnderMinimum ?? true
  const beschikbaar = berekenBeschikbaar(input.saldo, input.gereserveerd)
  const onderMinimum = isLaagInVoorraad(beschikbaar, input.minimumVoorraad)
  const positie = beschikbaar + Math.max(0, input.inBestelling)
  const positieOnderMinimum = isLaagInVoorraad(positie, input.minimumVoorraad)
  const aanvul = berekenAanvulAdvies({
    totaalSaldo: beschikbaar,
    minimumVoorraad: input.minimumVoorraad,
    maximumVoorraad: input.maximumVoorraad,
  })
  const basis = berekenBesteladvies({
    huidigeVoorraad: beschikbaar,
    // De drempel van de gedeelde functie is hier het aanvul-niveau: we vullen
    // aan tot het maximum (of het minimum), niet alleen tot het minimum.
    minimumVoorraad: aanvul.aanvullenTot,
    reedsBesteld: Math.max(0, input.inBestelling),
    // Afronden doen we hieronder op veelvouden van de besteleenheid; de gedeelde
    // functie zou bestelAantal als bodem gebruiken (één verpakking minimaal).
    bestelAantal: null,
    // Geen levertijd-verbruik: zie de JSDoc hierboven (spec §A1).
    gemiddeldVerbruikPerMaand: null,
    levertijdDagen: null,
  })
  const afgerond =
    alleenOnderMinimum && !positieOnderMinimum ? 0 : rondAfOpBesteleenheid(basis.adviesAantal, input.bestelAantal)
  // Plafond (spec 221 §6): nooit boven aanvullenTot + (besteleenheid - 1). Met de
  // afronding naar boven op één besteleenheid ligt het advies daar per definitie
  // onder; de klem is het vangnet als iemand de afronding ooit verandert.
  const eenheid = besteleenheid(input.bestelAantal)
  const plafond = Math.max(0, aanvul.aanvullenTot + eenheid - 1 - positie)
  const adviesAantal = Math.min(afgerond, Math.floor(plafond / eenheid) * eenheid)
  return {
    beschikbaar,
    onderMinimum,
    positie,
    positieOnderMinimum,
    aanvullenTot: aanvul.aanvullenTot,
    heeftMaximum: aanvul.heeftMaximum,
    tekort: basis.tekort,
    adviesAantal,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Minimumniveau-voorstel (afgekeurde regel, §E)
// ────────────────────────────────────────────────────────────────────────────

export interface MinimumVoorstel {
  /** Voorgesteld minimum (hele stuks, naar boven afgerond). */
  minimum: number
  /**
   * batch-69 (FO-521 §B8) — voorgesteld maximum via `stelMaximumVoor`
   * (minimum + verbruik per levertijd + besteleenheid); null bij minimum 0 (geen
   * voorraadbeleid meer, dus ook geen aanvulniveau).
   */
  maximum: number | null
  /** Gemiddeld verbruik per dag over de verbruiksperiode. */
  dagverbruik: number
  /** Levertijd + extra dekking. */
  dekkingDagen: number
  /** true als het voorstel afwijkt van het huidige minimum/gewenst. */
  wijziging: boolean
  /** Uitleg in één zin, zichtbaar in de UI en de audit (geen em-dash). */
  uitleg: string
}

function formatGetal(n: number): string {
  return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(n)
}

/**
 * Nieuw minimumniveau op basis van het werkelijke verbruik (spec A3):
 *
 *   minimum = verbruik 90 dagen / 90 x (levertijd + extra dekking), naar boven
 *
 * De extra dekking is instelbaar (default 7 dagen = "levertijd + 1 week"). Het
 * minimum is een signaleringsdrempel, dus hij wordt bewust NIET op de
 * verpakkingseenheid afgerond (een doos van 50 zou anders een minimum van 50
 * opleveren voor een artikel waarvan er 3 per maand gaan). Zonder verbruik is het
 * voorstel 0: geen signalering meer voor een artikel dat niet gebruikt wordt.
 */
export function stelMinimumVoor(
  verbruik90d: number,
  levertijdDagen: number | null | undefined,
  huidigMinimum: number,
  opties: { extraDekkingDagen?: number; huidigMaximum?: number | null; bestelAantal?: number | null } = {},
): MinimumVoorstel {
  const verbruik = Math.max(0, Number.isFinite(verbruik90d) ? verbruik90d : 0)
  const levertijd = Math.max(0, levertijdDagen ?? 0)
  const extra = Math.max(0, opties.extraDekkingDagen ?? 7)
  const dekkingDagen = levertijd + extra
  const dagverbruik = verbruik / VERBRUIK_PERIODE_DAGEN
  // Kleine epsilon: 18/90*20 = 3.9999999 mag geen 5 worden door afronding.
  const minimum = verbruik > 0 ? Math.max(1, Math.ceil(dagverbruik * dekkingDagen - 1e-9)) : 0

  const huidigMaximum = opties.huidigMaximum ?? null
  const maximum = minimum > 0 ? stelMaximumVoor(minimum, verbruik, levertijd, opties.bestelAantal) : null

  const wijziging = minimum !== huidigMinimum || maximum !== huidigMaximum

  const uitleg =
    verbruik > 0
      ? `Verbruik ${formatGetal(verbruik)} in ${VERBRUIK_PERIODE_DAGEN} dagen (${formatGetal(dagverbruik)} per dag) x ${dekkingDagen} dagen dekking (levertijd ${levertijd} + ${extra}) = minimum ${minimum}, maximum ${maximum ?? minimum}.`
      : `Geen verbruik in de afgelopen ${VERBRUIK_PERIODE_DAGEN} dagen: minimum 0 (geen signalering meer).`

  return { minimum, maximum, dagverbruik, dekkingDagen, wijziging, uitleg }
}

/**
 * batch-69 (FO-521 §B8, aanname A5) — voorstel voor het maximum (aanvulniveau):
 *
 *   maximum = minimum + verbruik tijdens de levertijd + één besteleenheid
 *
 * Het verbruik tijdens de levertijd is het dagverbruik over 90 dagen x de
 * levertijd, naar boven afgerond. Een minimum van 0 (geen beleid) geeft 0. Het
 * resultaat ligt altijd minstens één besteleenheid boven het minimum, zodat een
 * advies nooit op precies het minimum eindigt (dan zou het artikel na levering
 * nog steeds "laag" zijn).
 */
export function stelMaximumVoor(
  minimum: number,
  verbruik90d: number,
  levertijdDagen: number | null | undefined,
  bestelAantal: number | null | undefined,
): number {
  if (!Number.isFinite(minimum) || minimum <= 0) return 0
  const verbruik = Math.max(0, Number.isFinite(verbruik90d) ? verbruik90d : 0)
  const levertijd = Math.max(0, levertijdDagen ?? 0)
  const tijdensLevertijd = verbruik > 0 ? Math.ceil((verbruik / VERBRUIK_PERIODE_DAGEN) * levertijd - 1e-9) : 0
  return Math.floor(minimum) + Math.max(0, tijdensLevertijd) + besteleenheid(bestelAantal)
}

// ────────────────────────────────────────────────────────────────────────────
// batch-69 (FO-521 §C10) — automatisch bestellen (VOLLEDIG_AUTOMATISCH)
// ────────────────────────────────────────────────────────────────────────────

export type BesteladviesAutomatiseringsniveau = 'ADVIES_ALLEEN' | 'GOEDKEURING_HANDMATIG' | 'VOLLEDIG_AUTOMATISCH'

export interface AutoBestelRegelInput {
  /** Het te bestellen aantal (advies of aangepast). */
  aantal: number
  /** Prijs per eenheid als exacte decimaal-string ("12.50"); null = onbekend. */
  prijs: string | null
  /** De leverancier van het advies; null = verzamelbak zonder leverancier. */
  leverancierId: string | null
  artikelAutoBestellenUitgesloten: boolean
  /** Er staat een open telverschil of een lopende telling op het artikel (FO-467/FO-520). */
  openTelverschil: boolean
}

export interface AutoBestelInstelling {
  automatiseringsniveau: BesteladviesAutomatiseringsniveau
  /** Grens per regel (excl. btw) als decimaal-string; null = geen automatisch bestellen. */
  autoBestelMaxBedragPerRegel: string | null
  /** Wie de stand op VOLLEDIG_AUTOMATISCH zette: de order komt op diens naam. */
  automatiseringIngesteldDoorId: string | null
}

export interface AutoBestelOordeel {
  mag: boolean
  /** Waarom niet (leesbaar, voor de audit en de adviesregel). Leeg als `mag`. */
  redenen: string[]
  /** aantal x prijs (decimaal-string), of null zonder prijs. */
  bedrag: string | null
}

/**
 * Mag deze adviesregel zonder menselijke goedkeuring besteld worden (spec 221
 * §C10, VOLLEDIG_AUTOMATISCH)? Alleen als ALLES klopt; elke twijfel = nee, en de
 * regel valt terug op de goedkeuringenlijst:
 *   - de stand staat op VOLLEDIG_AUTOMATISCH, met een verantwoordelijke;
 *   - de prijs is bekend en er is een grens per regel, en het bedrag blijft eronder;
 *   - de leverancier is bekend en staat op automatisch bestellen;
 *   - het artikel is niet uitgesloten en heeft geen open telverschil.
 * De grens per advies (`autoBestelMaxBedragPerRun`) toetst `binnenRunGrens`.
 */
export function magAutomatischBestellen(
  regel: AutoBestelRegelInput,
  leverancier: { autoBestellen: boolean } | null,
  instelling: AutoBestelInstelling,
): AutoBestelOordeel {
  const redenen: string[] = []
  const bedrag = regel.prijs != null ? vermenigvuldigGeld(regel.prijs, regel.aantal) : null
  if (instelling.automatiseringsniveau !== 'VOLLEDIG_AUTOMATISCH') {
    redenen.push('De automatiseringsstand staat niet op volledig automatisch')
  } else if (!instelling.automatiseringIngesteldDoorId) {
    redenen.push('Er is geen verantwoordelijke voor het automatisch bestellen vastgelegd')
  }
  if (regel.aantal <= 0) redenen.push('Geen aantal om te bestellen')
  if (regel.prijs == null) redenen.push('Prijs onbekend')
  if (instelling.autoBestelMaxBedragPerRegel == null) {
    redenen.push('Geen bedraggrens per regel ingesteld')
  } else if (bedrag != null && vergelijkGeld(bedrag, instelling.autoBestelMaxBedragPerRegel) > 0) {
    redenen.push(`Regelbedrag boven de grens van ${formatEuroString(instelling.autoBestelMaxBedragPerRegel)}`)
  }
  if (!regel.leverancierId || !leverancier) redenen.push('Geen leverancier')
  else if (!leverancier.autoBestellen) redenen.push('Automatisch bestellen staat uit bij deze leverancier')
  if (regel.artikelAutoBestellenUitgesloten) redenen.push('Dit artikel is uitgesloten van automatisch bestellen')
  if (regel.openTelverschil) redenen.push('Er staat een open telverschil op het artikel')
  return { mag: redenen.length === 0, redenen, bedrag }
}

/**
 * De grens per advies: het totaal van de regels die automatisch besteld zouden
 * worden mag de grens niet overschrijden. Zonder grens: niets automatisch.
 */
export function binnenRunGrens(bedragen: readonly string[], maxPerRun: string | null): boolean {
  if (maxPerRun == null) return false
  return vergelijkGeld(telGeldOp(bedragen), maxPerRun) <= 0
}

// ────────────────────────────────────────────────────────────────────────────
// Exact rekenen met geld (decimaal-strings, BigInt in tienduizendsten)
// ────────────────────────────────────────────────────────────────────────────
//
// Prijzen staan als Prisma Decimal (10,2 of 12,4) in de database. De pure
// functies krijgen ze als string (Decimal.toFixed(4)) en rekenen exact in
// tienduizendsten van een euro: geen float-afronding tussendoor, afronden pas in
// de presentatie (CLAUDE.md "rond pas op het laatste moment"). BigInt-constanten
// via `BigInt(...)` zoals in indexatie.ts.

const SCHAAL = BigInt(10000)
const NUL = BigInt(0)

/** Een decimaal-string ("12.5", "-3", "0.1234") naar tienduizendsten. */
export function geldNaarEenheden(waarde: string): bigint {
  const t = waarde.trim()
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(t)
  if (!m) throw new Error(`Ongeldig bedrag: "${waarde}"`)
  const [, min, heel, frac = ''] = m
  // Meer dan 4 decimalen: half-up afronden op 4 (komt bij Decimal(12,4) niet voor).
  const vier = (frac + '0000').slice(0, 4)
  let eenheden = BigInt(heel!) * SCHAAL + BigInt(vier)
  if (frac.length > 4 && Number(frac[4]) >= 5) eenheden += BigInt(1)
  return min ? -eenheden : eenheden
}

/** Tienduizendsten naar een decimaal-string met 4 decimalen ("12.5000"). */
export function eenhedenNaarGeld(eenheden: bigint): string {
  const negatief = eenheden < NUL
  const abs = negatief ? -eenheden : eenheden
  const heel = abs / SCHAAL
  const frac = (abs % SCHAAL).toString().padStart(4, '0')
  return `${negatief ? '-' : ''}${heel.toString()}.${frac}`
}

/** prijs x aantal, exact. */
export function vermenigvuldigGeld(prijs: string, aantal: number): string {
  if (!Number.isInteger(aantal)) throw new Error(`Aantal moet heel zijn: ${aantal}`)
  return eenhedenNaarGeld(geldNaarEenheden(prijs) * BigInt(aantal))
}

/** Som van bedragen, exact. */
export function telGeldOp(bedragen: Iterable<string>): string {
  let som = NUL
  for (const b of bedragen) som += geldNaarEenheden(b)
  return eenhedenNaarGeld(som)
}

/** -1 / 0 / 1 zoals a < b, a = b, a > b. */
export function vergelijkGeld(a: string, b: string): number {
  const x = geldNaarEenheden(a)
  const y = geldNaarEenheden(b)
  return x < y ? -1 : x > y ? 1 : 0
}

/** Afronden op centen (half-up, weg van nul), pas in de presentatie. */
export function rondGeldAfOpCenten(bedrag: string): string {
  const e = geldNaarEenheden(bedrag)
  const honderd = BigInt(100)
  const negatief = e < NUL
  const abs = negatief ? -e : e
  const centen = (abs + BigInt(50)) / honderd
  const uit = centen * honderd
  return eenhedenNaarGeld(negatief ? -uit : uit)
}

/** Een bedrag als getal in euro's met centen (alleen voor DTO's/presentatie). */
export function geldNaarGetal(bedrag: string): number {
  return Number(rondGeldAfOpCenten(bedrag))
}

const EURO = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })

/** "€ 1.234,56" uit een decimaal-string. */
export function formatEuroString(bedrag: string): string {
  return EURO.format(geldNaarGetal(bedrag))
}

// ────────────────────────────────────────────────────────────────────────────
// batch-69 (FO-521 §C13) — werkdagen tellen (herinnering/escalatie)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Aantal volle werkdagen NA `vanYmd` tot en met `totYmd` (ma-vr, geen
 * feestdag). Een advies van maandag is op woensdag 2 werkdagen oud; over een
 * weekend tellen alleen de werkdagen. Negatief bereik = 0.
 */
export function werkdagenSinds(vanYmd: string, totYmd: string, feestdagenYmd: ReadonlySet<string> = new Set()): number {
  const dagen = dagenTussenYmd(vanYmd, totYmd)
  if (dagen <= 0) return 0
  let n = 0
  for (let i = 1; i <= dagen; i++) {
    if (isNlWerkdagYmd(plusDagenYmd(vanYmd, i), feestdagenYmd)) n++
  }
  return n
}

// ────────────────────────────────────────────────────────────────────────────
// Stilteperiode
// ────────────────────────────────────────────────────────────────────────────

const DAG_MS = 24 * 60 * 60 * 1000

/** Einde van de stilteperiode vanaf `nu`. */
export function stilteperiodeTot(nu: Date, stilteperiodeDagen: number): Date {
  return new Date(nu.getTime() + Math.max(0, stilteperiodeDagen) * DAG_MS)
}

/**
 * Houdt de stilteperiode het artikel (nog) uit het advies? Nee zodra de periode
 * verstreken is, of zodra de beschikbare voorraad verder gedaald is dan bij het
 * afkeuren (spec §E9 "of bij verdere daling").
 */
export function isMinimumVoorstelOnderdrukt(input: {
  onderdruktTot: Date | null
  nu: Date
  beschikbaarNu: number
  beschikbaarBijAfkeur: number | null
}): boolean {
  if (!input.onderdruktTot || input.onderdruktTot.getTime() <= input.nu.getTime()) return false
  if (input.beschikbaarBijAfkeur != null && input.beschikbaarNu < input.beschikbaarBijAfkeur) return false
  return true
}

// ────────────────────────────────────────────────────────────────────────────
// NL-datums, werkdagen en het run-venster
// ────────────────────────────────────────────────────────────────────────────

/** NL-kalenderdag (yyyy-mm-dd) van een instant. */
export function nlDatum(nu: Date): string {
  return utcToAmsterdamWallclock(nu).ymd
}

/** Aantal kalenderdagen van `van` tot `tot` (tot - van); negatief als tot eerder ligt. */
export function dagenTussenYmd(van: string, tot: string): number {
  const [j1, m1, d1] = van.split('-').map(Number)
  const [j2, m2, d2] = tot.split('-').map(Number)
  return Math.round((Date.UTC(j2!, m2! - 1, d2!) - Date.UTC(j1!, m1! - 1, d1!)) / DAG_MS)
}

/** ISO-weekdag (1 = maandag ... 7 = zondag) van een yyyy-mm-dd. */
export function isoWeekdagYmd(ymd: string): number {
  const [j, m, d] = ymd.split('-').map(Number)
  const dag = new Date(Date.UTC(j!, m! - 1, d!)).getUTCDay()
  return dag === 0 ? 7 : dag
}

/** Werkdag = maandag t/m vrijdag en geen (verplichte) feestdag. */
export function isNlWerkdagYmd(ymd: string, feestdagenYmd: ReadonlySet<string> = new Set()): boolean {
  return isoWeekdagYmd(ymd) <= 5 && !feestdagenYmd.has(ymd)
}

/** Verwachte leverdatum (yyyy-mm-dd) = besteldatum + levertijd in kalenderdagen. */
export function verwachteLeverdatum(besteldOpYmd: string, levertijdDagen: number | null | undefined): string {
  return plusDagenYmd(besteldOpYmd, Math.max(0, levertijdDagen ?? 0))
}

/** Te laat = de verwachte leverdatum ligt vóór vandaag (NL) en er is nog niet alles ontvangen. */
export function isLeveringTeLaat(verwachtYmd: string | null, vandaagYmd: string): boolean {
  if (!verwachtYmd) return false
  return verwachtYmd < vandaagYmd
}

export type BesteladviesRunBeslissing =
  | { draaien: true; datum: string }
  | { draaien: false; reden: 'UITGESCHAKELD' | 'GEEN_WERKDAG' | 'NOG_NIET' | 'AL_GEDRAAID'; datum: string }

/**
 * Moet de dagelijkse besteladvies-run nu starten? De tick loopt elk kwartier;
 * de run start één keer per NL-dag zodra het ingestelde tijdstip gepasseerd is
 * (op werkdagen als `alleenWerkdagen`). `laatsteRunDatum` is de NL-datum van de
 * laatst geslaagde run.
 */
export function moetBesteladviesRunDraaien(input: {
  nu: Date
  actief: boolean
  runTijd: string
  alleenWerkdagen: boolean
  laatsteRunDatum: string | null
  feestdagenYmd?: ReadonlySet<string>
}): BesteladviesRunBeslissing {
  const { ymd, hm } = utcToAmsterdamWallclock(input.nu)
  if (!input.actief) return { draaien: false, reden: 'UITGESCHAKELD', datum: ymd }
  if (input.alleenWerkdagen && !isNlWerkdagYmd(ymd, input.feestdagenYmd)) {
    return { draaien: false, reden: 'GEEN_WERKDAG', datum: ymd }
  }
  if (hm < input.runTijd) return { draaien: false, reden: 'NOG_NIET', datum: ymd }
  if (input.laatsteRunDatum === ymd) return { draaien: false, reden: 'AL_GEDRAAID', datum: ymd }
  return { draaien: true, datum: ymd }
}

// ────────────────────────────────────────────────────────────────────────────
// Nummering + bestelbericht
// ────────────────────────────────────────────────────────────────────────────

/** Bouw het volgende besteladviesnummer "BA-YYYY-NNNNN" (5-cijferig). */
export function buildBesteladviesNummer(year: number, highestN: number): string {
  return `BA-${year}-${String(highestN + 1).padStart(5, '0')}`
}

/** Sequentienummer uit "BA-2026-00012"; 0 als het niet matcht. */
export function parseBesteladviesNummer(code: string): number {
  const match = code.match(/^BA-\d{4}-(\d+)$/)
  if (!match) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isFinite(n) ? n : 0
}

export interface BestelberichtRegel {
  aantal: number
  naam: string
  eenheid?: string | null
  leverancierArtNr?: string | null
}

/** "dd-MM-yyyy" van een yyyy-mm-dd (UI-conventie). */
export function ymdNaarNl(ymd: string): string {
  const [j, m, d] = ymd.split('-')
  return `${d}-${m}-${j}`
}

/**
 * Onderwerp + platte tekst van het bestelbericht aan de leverancier. Merknaam
 * STREEP, geen em-dash; het ordernummer staat erin zodat de pakbon en de factuur
 * automatisch aan de order te koppelen zijn.
 */
export function renderBestelbericht(input: {
  leverancierNaam: string
  poNummer: string
  regels: readonly BestelberichtRegel[]
  verwachteLeverdatumYmd: string | null
  opmerking?: string | null
}): { onderwerp: string; body: string } {
  const regelLijnen = input.regels.map((r) => {
    const eenheid = r.eenheid ? ` ${r.eenheid}` : ''
    const artNr = r.leverancierArtNr ? ` (art.nr ${r.leverancierArtNr})` : ''
    return `- ${r.aantal}${eenheid} x ${r.naam}${artNr}`
  })
  const onderwerp = `Bestelling ${input.poNummer} - STREEP`
  const regels: string[] = [
    `Beste ${input.leverancierNaam},`,
    '',
    `Hierbij bestellen wij onder ordernummer ${input.poNummer}:`,
    '',
    ...(regelLijnen.length > 0 ? regelLijnen : ['- (geen regels)']),
    '',
  ]
  if (input.verwachteLeverdatumYmd) {
    regels.push(`Gewenste leverdatum: ${ymdNaarNl(input.verwachteLeverdatumYmd)}.`)
  }
  regels.push(`Vermeld ons ordernummer ${input.poNummer} graag op de pakbon en de factuur.`)
  if (input.opmerking && input.opmerking.trim().length > 0) {
    regels.push('', input.opmerking.trim())
  }
  regels.push('', 'Met vriendelijke groet,', 'STREEP Inkoop')
  return { onderwerp, body: regels.join('\n') }
}
